import { useCallback, useEffect, useMemo, useState } from "react";
import { ListGroup, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import { ACCESS_RIGHT_AREAS } from "../../../constant/permissions";
import {
	useListUserProjectsQuery,
	useListUsersQuery,
	useSetUserStatusMutation,
} from "../../../store/services/adminUsers";
import {
	createUser,
	deleteUser,
	updateUser,
} from "../../../store/slices/userSlice";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import isGlobalAdminRole from "../../../utils/isGlobalAdmin";
import { showToast } from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddUserModal from "./add/add-user";
import {
	getUserTableColumns,
	getUserTypeChipFilter,
} from "./constant.js";
import "./user.scss";

function UserSettingPage() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { user: currentUser } = useSelector((state) => state.auth);
	const { loading: userMutationLoading } = useSelector((state) => state.users);

	const isGlobalAdmin = isGlobalAdminRole(currentUser?.role);
	const [activeType, setActiveType] = useState("all");

	const {
		data,
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useListUsersQuery(isGlobalAdmin ? activeType : "all");

	const users = useMemo(() => data?.items ?? [], [data?.items]);

	const [userModalView, setUserModalView] = useState("");
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [selectedUserData, setSelectedUserData] = useState({});
	const [filteredUserList, setFilteredUserList] = useState([]);
	const buildFilterItems = useCallback(
		(userTypeValue, searchValue = "") => {
			const prepend = isGlobalAdmin
				? [getUserTypeChipFilter(userTypeValue)]
				: [];
			const base = getSearchAddFilter({ addTextKey: "users.add-user" });
			return [
				...base.map((item) =>
					item.key === "search"
						? { ...item, value: searchValue }
						: item,
				),
				...prepend,
			];
		},
		[isGlobalAdmin],
	);

	const [filterItems, setFilterItems] = useState(() =>
		buildFilterItems("all"),
	);

	useEffect(() => {
		setFilterItems((prev) => {
			const searchValue =
				prev.find((item) => item.key === "search")?.value ?? "";
			return buildFilterItems(activeType, searchValue);
		});
	}, [activeType, buildFilterItems]);
	const [showProjectsModal, setShowProjectsModal] = useState(false);
	const [projectsModalUser, setProjectsModalUser] = useState(null);
	const [statusTarget, setStatusTarget] = useState(null);
	const [setUserStatus, { isLoading: statusUpdating }] =
		useSetUserStatusMutation();

	const {
		data: userProjectsData,
		isFetching: projectsLoading,
	} = useListUserProjectsQuery(projectsModalUser?.id, {
		skip: !showProjectsModal || !projectsModalUser?.id,
	});
	const userProjects = useMemo(
		() => userProjectsData?.items ?? [],
		[userProjectsData?.items],
	);

	useEffect(() => {
		if (isError) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	}, [isError, error, t]);

	useEffect(() => {
		setFilteredUserList(users);
	}, [users]);

const onProjectsClick = useCallback((row) => {
		if (!row?.id || !(row.projectCount > 0)) return;
		setProjectsModalUser(row);
		setShowProjectsModal(true);
	}, []);

	const onUserEdit = useCallback((row) => {
		setSelectedUserData({ ...row });
		setUserModalView("edit");
	}, []);

	const onUserDelete = useCallback((row) => {
		setSelectedUserData(row);
		setShowDeleteModal(true);
	}, []);

	const onToggleStatus = useCallback((row) => {
		setStatusTarget(row);
	}, []);

	const onConfirmToggleStatus = async () => {
		if (!statusTarget?.id) return;
		try {
			await setUserStatus({
				id: statusTarget.id,
				isActive: statusTarget.isActive === false,
			}).unwrap();
			showToast(t("USER.STATUS_UPDATE_SUCCESS"), { type: "success" });
			setStatusTarget(null);
			refetch();
		} catch (err) {
			showToast(err?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const userColumns = useMemo(
		() =>
			getUserTableColumns(
				t,
				onUserEdit,
				onUserDelete,
				currentUser?.id,
				{
					isGlobalAdmin,
					onProjectsClick: isGlobalAdmin ? onProjectsClick : undefined,
					onToggleStatus,
				},
			),
		[
			t,
			onUserEdit,
			onUserDelete,
			currentUser?.id,
			isGlobalAdmin,
			onProjectsClick,
			onToggleStatus,
		],
	);

	const onDeleteUser = async () => {
		try {
			await dispatch(deleteUser(selectedUserData?.id)).unwrap();
			setShowDeleteModal(false);
			showToast(t("users.delete-success"), { type: "success" });
			refetch();
		} catch (err) {
			showToast(err || t("errors.general"), { type: "error" });
		}
	};

	const onAddUser = async (userInfo) => {
		try {
			if (userModalView === "add") {
				await dispatch(createUser(userInfo)).unwrap();
				showToast(t("users.save-success"), { type: "success" });
			} else if (userModalView === "edit") {
				await dispatch(
					updateUser({ ...userInfo, id: selectedUserData.id }),
				).unwrap();
				showToast(t("users.update-success"), { type: "success" });
			}
			await refetch();
			setUserModalView("");
		} catch (err) {
			showToast(err || t("errors.general"), { type: "error" });
		}
	};

	const onFilterAction = (index, key, info) => {
		if (key === "userType" && info?.value) {
			setActiveType(info.value);
		}

		const localFilterItems = [...filterItems];
		if (info?.value !== undefined) {
			localFilterItems[index].value = info.value;
		}
		setFilterItems(localFilterItems);

		let filtered = [...users];
		localFilterItems.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(
					item.value,
					filtered,
					userColumns.slice(0, -1),
				);
			}
		});
		setFilteredUserList(filtered);

		if (key === "add") {
			setSelectedUserData({});
			setUserModalView("add");
		}
	};

	const listLoading = isLoading || isFetching;

	return (
		<div className="page-container settings-users">
			<FilterBar
				items={filterItems}
				onFilterAction={onFilterAction}
				area={ACCESS_RIGHT_AREAS.USERS}
			/>

			<div className="flex-grow-1 ag-grid-parent settings-users__grid">
				{listLoading && filteredUserList.length === 0 ? (
					<TableSkeleton />
				) : (
					<AgGridAtom
						columnDefs={userColumns}
						rowData={filteredUserList}
						context={{
							permissions: currentUser?.permissions || [],
							currentUserId: currentUser?.id,
							area: ACCESS_RIGHT_AREAS.USERS,
							userPermissions: currentUser?.permissions || [],
						}}
						enableBrowserTooltips
						overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${users.length === 0 ? t("users.no-data") : t("common.no-results-found")}</span>`}
						defaultColDef={{
							flex: 1,
							sortable: true,
							filter: false,
						}}
					/>
				)}
			</div>

			{userModalView && (
				<AddUserModal
					mode={userModalView}
					userInfo={selectedUserData}
					show
					onClose={() => {
						setUserModalView("");
						setSelectedUserData({});
					}}
					onSubmit={onAddUser}
					loading={
						userMutationLoading.create ||
						userMutationLoading.update
					}
				/>
			)}

			<ModalAtom
				title={t("users.delete-user")}
				body={<p>{t("users.delete-user-warning")}</p>}
				saveText={
					userMutationLoading.delete
						? t("common.saving")
						: t("delete")
				}
				show={showDeleteModal}
				handleClose={() => setShowDeleteModal(false)}
				handleSubmit={onDeleteUser}
				disabled={userMutationLoading.delete}
			/>

			<ModalAtom
				title={
					statusTarget?.isActive === false
						? t("users.activate-user")
						: t("users.deactivate-user")
				}
				body={
					<p>
						{statusTarget?.isActive === false
							? t("users.activate-user-warning")
							: t("users.deactivate-user-warning")}
					</p>
				}
				saveText={
					statusUpdating
						? t("common.saving")
						: statusTarget?.isActive === false
							? t("users.activate")
							: t("users.deactivate")
				}
				show={!!statusTarget}
				handleClose={() => setStatusTarget(null)}
				handleSubmit={onConfirmToggleStatus}
				disabled={statusUpdating}
			/>

			{isGlobalAdmin && (
				<ModalAtom
					show={showProjectsModal}
					handleClose={() => {
						setShowProjectsModal(false);
						setProjectsModalUser(null);
					}}
					title={t("admin.users.projects-title")}
					hideSaveButton
					body={
						<div>
							{projectsModalUser && (
								<p className="text-muted mb-3">
									{projectsModalUser.firstName}{" "}
									{projectsModalUser.lastName} -{" "}
									{projectsModalUser.email}
								</p>
							)}
							{projectsLoading ? (
								<div className="text-center py-4">
									<Spinner animation="border" size="sm" />{" "}
									{t("admin.users.projects-loading")}
								</div>
							) : userProjects.length === 0 ? (
								<p className="mb-0">
									{t("admin.users.projects-empty")}
								</p>
							) : (
								<ListGroup className="settings-users__projects-list">
									{userProjects.map((proj) => (
										<ListGroup.Item
											key={proj.id}
											action
											as={Link}
											to={`/projects/${proj.id}/members`}
											onClick={() =>
												setShowProjectsModal(false)
											}
										>
											{proj.name}
										</ListGroup.Item>
									))}
								</ListGroup>
							)}
						</div>
					}
				/>
			)}
		</div>
	);
}

export default UserSettingPage;
